/**
 * JupyterHub Token Service
 * Handles token-based authentication for seamless workspace switching (chuẩn hóa RTK Query)
 */

import config from '../config/domains';
import { store } from '../store';
import apiSlice from '../store/slices/apiSlice';

class JupyterHubTokenService {
    /**
     * Generate JupyterHub access token for a workspace
     * @param {string} projectName - Project/workspace name
     * @returns {Promise<Object>} Token response
     */
    async generateToken(projectName) {
        try {
            const user_token = localStorage.getItem('user_token');
            return await store
                .dispatch(apiSlice.endpoints.jupyterhubGenerateToken.initiate({ user_token, project_name: projectName }))
                .unwrap();
        } catch (error) {
            console.error('Error generating JupyterHub token:', error);
            throw error;
        }
    }

    /**
     * Get JupyterHub URL with token for seamless access
     * @param {string} projectName - Project/workspace name
     * @returns {Promise<Object>} URL response with token
     */
    async getWorkspaceUrl(projectName) {
        try {
            const user_token = localStorage.getItem('user_token');
            const result = await store.dispatch(
                apiSlice.endpoints.jupyterhubWorkspaceUrl.initiate({ user_token, project_name: projectName })
            );
            if (result.isError && result.error) throw result.error;
            return result.data;
        } catch (error) {
            console.error('Error getting workspace URL:', error);
            throw error;
        }
    }

    /**
     * Open JupyterHub workspace in new tab with automatic login
     * @param {string} projectName - Project/workspace name
     */
    async openWorkspace(projectName) {
        try {
            // Get workspace URL with JupyterHub native token
            // Generate token and open with it
            const { token, jupyterhub_username } = await this.generateToken(projectName);

            // Build URL with token for auto-login
            const jupyterBase = config.JUPYTER_URL;

            // Try direct lab URL with token first
            const url = `${jupyterBase}/hub/user/${jupyterhub_username}/lab?token=${token}`;

            console.log('Opening JupyterHub with URL:', url);

            // Open in new tab
            window.open(url, '_blank');
        } catch (error) {
            console.error('Error opening workspace:', error);
            // Fallback to traditional login
            this.openWorkspaceWithLogin(projectName);
        }
    }

    /**
     * Open JupyterHub workspace in iframe with token
     * @param {string} projectName - Project/workspace name
     * @returns {Promise<string>} Iframe URL
     */
    async getIframeUrl(projectName) {
        try {
            const { token, jupyterhub_username } = await this.generateToken(projectName);

            // Build iframe URL with token
            const jupyterBase = config.JUPYTER_URL || 'https://datahub.n2nai.io/jupyter';

            // Use hub login URL for iframe to ensure proper authentication
            // The next parameter should include the full path with /jupyter prefix
            const iframeUrl = `${jupyterBase}/hub/login?token=${token}&next=${encodeURIComponent(`/jupyter/hub/user/${jupyterhub_username}/lab`)}`;

            console.log('JupyterHub iframe URL:', iframeUrl);

            return iframeUrl;
        } catch (error) {
            console.error('Error getting iframe URL:', error);
            throw error;
        }
    }

    /**
     * Open workspace with auto-filled login form
     * @param {string} projectName - Project/workspace name
     */
    async openWorkspaceWithLogin(projectName) {
        try {
            const userToken = localStorage.getItem('user_token');
            const username = `${userToken}_${projectName}`.toLowerCase();

            // Get project info to get JupyterHub password
            const projectInfo = await this.getProjectInfo(userToken, projectName);
            const password = projectInfo.mongodb_passwd; // JupyterHub uses same password as MongoDB

            // Create and submit an auto-login form
            this.submitAutoLoginForm(username, password, projectName);
        } catch (error) {
            console.error('Error getting project info for auto-login:', error);
            // Final fallback - just open login page
            const userToken = localStorage.getItem('user_token');
            const username = `${userToken}_${projectName}`.toLowerCase();
            const jupyterBase = process.env.REACT_APP_JUPYTER_URL || 'https://datahub.n2nai.io/jupyter';
            const loginUrl = `${jupyterBase}/hub/login?next=/hub/user/${username}/lab`;
            window.open(loginUrl, '_blank');
        }
    }

    /**
     * Get project information from API (RTK Query getProjects)
     * @param {string} userToken - User token
     * @param {string} projectName - Project name
     * @returns {Promise<Object|null>} Project info
     */
    async getProjectInfo(userToken, projectName) {
        try {
            const result = await store.dispatch(apiSlice.endpoints.getProjects.initiate(userToken || undefined));
            if (result.isError) return null;
            const list = result.data;
            const projects = Array.isArray(list) ? list : list?.projects ?? [];
            const project = projects.find(
                (p) => p.project_name?.toLowerCase() === projectName.toLowerCase() || p.name?.toLowerCase() === projectName.toLowerCase()
            );
            return project || null;
        } catch (error) {
            console.error('Error getting project info:', error);
            return null;
        }
    }

    /**
     * Generate simple password for JupyterHub
     * @param {string} userToken - User token
     * @returns {string} Generated password
     */
    generatePassword(userToken) {
        // Simple password generation based on user token
        // In production, this should be more secure
        return `${userToken.substring(0, 8)}123`;
    }

    /**
     * Submit auto-login form to JupyterHub
     * @param {string} username - JupyterHub username
     * @param {string} password - JupyterHub password
     * @param {string} projectName - Project name
     */
    async submitAutoLoginForm(username, password, projectName) {
        try {
            // First, get the login page to extract XSRF token
            const loginPageUrl = 'https://datahub.n2nai.io/jupyter/hub/login';
            const loginWindow = window.open(loginPageUrl, '_blank');

            // Wait a bit for the page to load, then try to auto-fill
            setTimeout(() => {
                try {
                    // Note: Due to CORS, we can't directly manipulate the opened window
                    // This is a limitation of cross-origin security
                    console.log(`Auto-login attempted for ${username}`);
                    console.log(`Please manually enter password if needed: ${password.substring(0, 4)}...`);
                } catch (e) {
                    console.log('Could not auto-fill due to cross-origin restrictions');
                }
            }, 2000);
        } catch (error) {
            console.error('Error with auto-login:', error);
            // Fallback: just open the login page
            const loginUrl = `https://datahub.n2nai.io/jupyter/hub/login?next=/hub/user/${username}/lab`;
            window.open(loginUrl, '_blank');
        }
    }

    /**
     * Check if token is still valid
     * @param {string} token - JupyterHub token
     * @returns {Promise<boolean>} True if valid
     */
    async validateToken(token) {
        try {
            const result = await store.dispatch(apiSlice.endpoints.jupyterhubValidateToken.initiate({ token })).unwrap();
            return result?.valid === true;
        } catch (error) {
            console.error('Error validating token:', error);
            return false;
        }
    }

    /**
     * Store token in localStorage for reuse
     * @param {string} projectName - Project name
     * @param {string} token - JupyterHub token
     */
    storeToken(projectName, token) {
        const key = `jupyterhub_token_${projectName}`;
        const data = {
            token,
            timestamp: Date.now()
        };
        localStorage.setItem(key, JSON.stringify(data));
    }

    /**
     * Get stored token if still valid
     * @param {string} projectName - Project name
     * @returns {string|null} Token or null if expired
     */
    getStoredToken(projectName) {
        const key = `jupyterhub_token_${projectName}`;
        const stored = localStorage.getItem(key);

        if (!stored) return null;

        try {
            const data = JSON.parse(stored);
            const age = Date.now() - data.timestamp;

            // Token expires after 50 minutes (to be safe)
            if (age > 50 * 60 * 1000) {
                localStorage.removeItem(key);
                return null;
            }

            return data.token;
        } catch (error) {
            return null;
        }
    }
}

export default new JupyterHubTokenService();
